<template>
  <div class="article-text-container" ref="contentContainer"></div>
</template>

<script setup>
import { ref, onMounted, nextTick, getCurrentInstance } from 'vue'
import { createApp } from 'vue'
import PaymentCTA from './PaymentCTA.vue'
import MinimalCTA from './MinimalCTA.vue'
import BlogImageGallery from './BlogImageGallery.vue'
import BlogSocialFollow from './BlogSocialFollow.vue'

const props = defineProps({
  content: {
    type: String,
    required: true
  }
})

const contentContainer = ref(null)

onMounted(async () => {
  try {
    // Set HTML content
    contentContainer.value.innerHTML = props.content
    
    await nextTick()
    
    // Get the parent app's pinia instance
    const instance = getCurrentInstance()
    const parentApp = instance?.appContext?.app
    const pinia = parentApp?._context?.provides?.pinia || parentApp?.config?.globalProperties?.$pinia
    
    // Mount Payment CTAs
    contentContainer.value.querySelectorAll('.payment-cta-embed').forEach(el => {
      try {
        const app = createApp(PaymentCTA, {
          planKey: el.dataset.planKey,
          billingId: el.dataset.billingId,
          title: el.dataset.title,
          description: el.dataset.description,
          buttonText: el.dataset.buttonText
        })
        if (pinia) {
          app.use(pinia)
        }
        app.mount(el)
      } catch (err) {
        console.error('Failed to mount PaymentCTA:', err)
        el.innerHTML = '<div class="content-box warning-box"><i class="icon-warning"></i><div class="box-content">Payment option temporarily unavailable</div></div>'
      }
    })

    // Mount Minimal CTAs
    contentContainer.value.querySelectorAll('.minimal-cta-embed').forEach(el => {
      try {
        const app = createApp(MinimalCTA, {
          text: el.dataset.text,
          buttonText: el.dataset.buttonText,
          href: el.dataset.href,
          buttonVariant: el.dataset.buttonVariant,
          buttonSize: el.dataset.buttonSize,
          buttonIcon: el.dataset.buttonIcon,
          iconPosition: el.dataset.iconPosition || 'right',
          align: el.dataset.align,
          background: el.dataset.background
        })
        if (pinia) {
          app.use(pinia)
        }
        app.mount(el)
      } catch (err) {
        console.error('Failed to mount MinimalCTA:', err)
        el.innerHTML = '<div class="content-box warning-box"><i class="icon-warning"></i><div class="box-content">CTA temporarily unavailable</div></div>'
      }
    })
    
    // Initialize Image Galleries
    contentContainer.value.querySelectorAll('.image-gallery').forEach(el => {
      try {
        const images = Array.from(el.querySelectorAll('img')).map(img => ({
          url: img.src,
          alt: img.alt,
          caption: img.dataset.caption || ''
        }))
        const galleryApp = createApp(BlogImageGallery, { images })
        el.innerHTML = '' // Clear original images
        galleryApp.mount(el)
      } catch (err) {
        console.error('Failed to mount BlogImageGallery:', err)
        // Keep original images as fallback
      }
    })
    
    // Initialize Social Follow embeds
    contentContainer.value.querySelectorAll('.social-follow-embed').forEach(el => {
      try {
        const app = createApp(BlogSocialFollow, {
          title: el.dataset.title,
          description: el.dataset.description
        })
        app.mount(el)
      } catch (err) {
        console.error('Failed to mount BlogSocialFollow:', err)
        el.innerHTML = '<div class="content-box warning-box"><i class="icon-warning"></i><div class="box-content">Social links temporarily unavailable</div></div>'
      }
    })
    
    // Load social embeds
    loadSocialEmbeds(contentContainer.value)
    
    // Initialize lightbox functionality for advanced images
    initLightboxes()
    
  } catch (error) {
    console.error('Error mounting components:', error)
  }
})

function loadSocialEmbeds(container) {
  if (!container) return
  
  // Handle Twitter embeds
  const twitterEmbeds = container.querySelectorAll('.twitter-embed')
  if (twitterEmbeds.length > 0 && !window.twttr) {
    const script = document.createElement('script')
    script.src = 'https://platform.twitter.com/widgets.js'
    script.async = true
    script.onload = () => {
      twitterEmbeds.forEach(embed => {
        const embedId = embed.dataset.embedId
        if (embedId && window.twttr?.widgets) {
          embed.innerHTML = ''
          window.twttr.widgets.createTweet(
            embedId,
            embed,
            { theme: 'light', width: 550 }
          )
        }
      })
    }
    document.body.appendChild(script)
  } else if (twitterEmbeds.length > 0 && window.twttr?.widgets) {
    twitterEmbeds.forEach(embed => {
      const embedId = embed.dataset.embedId
      if (embedId) {
        embed.innerHTML = ''
        window.twttr.widgets.createTweet(
          embedId,
          embed,
          { theme: 'light', width: 550 }
        )
      }
    })
  }

  // Handle YouTube embeds
  container.querySelectorAll('.youtube-embed').forEach(embed => {
    const url = embed.dataset.url
    if (url) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?]*)/)?.[1]
      if (videoId) {
        embed.innerHTML = `
          <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
            <iframe src="https://www.youtube.com/embed/${videoId}" 
              style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>`
      }
    }
  })

  // Handle Instagram embeds
  const instagramEmbeds = container.querySelectorAll('.instagram-embed')
  if (instagramEmbeds.length > 0) {
    instagramEmbeds.forEach(embed => {
      const url = embed.dataset.url
      const embedId = embed.dataset.embedId
      const embedType = embed.dataset.embedType || 'p'
      if (embedId) {
        // Instagram requires their embed script
        embed.innerHTML = `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14"></blockquote>`
      }
    })
    
    // Load Instagram embed script
    if (!window.instgrm) {
      const script = document.createElement('script')
      script.src = '//www.instagram.com/embed.js'
      script.async = true
      script.onload = () => {
        if (window.instgrm?.Embeds) {
          window.instgrm.Embeds.process()
        }
      }
      document.body.appendChild(script)
    } else if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process()
    }
  }

  // Handle Facebook embeds
  container.querySelectorAll('.facebook-embed').forEach(embed => {
    const url = embed.dataset.url
    if (url) {
      embed.innerHTML = `
        <iframe src="https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500" 
          width="500" 
          height="600" 
          style="border:none;overflow:hidden" 
          scrolling="no" 
          frameborder="0" 
          allowfullscreen="true" 
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share">
        </iframe>`
    }
  })

  // Handle TikTok embeds
  container.querySelectorAll('.tiktok-embed').forEach(embed => {
    const url = embed.dataset.url
    if (url) {
      const videoId = url.match(/video\/(\d+)/)?.[1]
      if (videoId) {
        embed.innerHTML = `
          <blockquote class="tiktok-embed" cite="${url}" data-video-id="${videoId}" style="max-width: 605px;min-width: 325px;">
            <section></section>
          </blockquote>`
        
        const script = document.createElement('script')
        script.src = 'https://www.tiktok.com/embed.js'
        script.async = true
        document.body.appendChild(script)
      }
    }
  })

  // Handle LinkedIn embeds
  container.querySelectorAll('.linkedin-embed').forEach(embed => {
    const url = embed.dataset.url
    if (url) {
      embed.innerHTML = `
        <iframe src="https://www.linkedin.com/embed/feed/update/${encodeURIComponent(url)}" 
          height="600" 
          width="500" 
          frameborder="0" 
          allowfullscreen="" 
          title="Embedded post">
        </iframe>`
    }
  })

}

function initLightboxes() {
  document.querySelectorAll('.advanced-image.lightbox-enabled img').forEach(img => {
    img.addEventListener('click', openLightbox)
  })
}

function openLightbox(event) {
  const img = event.target
  const src = img.src
  const alt = img.alt
  
  const lightbox = document.createElement('div')
  lightbox.className = 'simple-lightbox'
  lightbox.innerHTML = `
    <div class="lightbox-backdrop"></div>
    <div class="lightbox-content">
      <img src="${src}" alt="${alt}">
      <button class="lightbox-close" aria-label="Close">×</button>
    </div>
  `
  
  document.body.appendChild(lightbox)
  
  // Trigger animation
  requestAnimationFrame(() => {
    lightbox.classList.add('active')
  })
  
  // Close handlers
  const closeHandler = () => {
    lightbox.classList.remove('active')
    setTimeout(() => lightbox.remove(), 300)
  }
  
  lightbox.querySelector('.lightbox-backdrop').addEventListener('click', closeHandler)
  lightbox.querySelector('.lightbox-close').addEventListener('click', closeHandler)
  
  // Escape key handler
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      closeHandler()
      document.removeEventListener('keydown', escapeHandler)
    }
  }
  document.addEventListener('keydown', escapeHandler)
}
</script>